import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/utils/toast';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useComposerStore } from '@/stores/composerStore';
import { usePost } from './usePosts';
import { useAutosave } from './useAutosave';
import { useServerDraftAutosave } from './useServerDraftAutosave';
import { useEffectiveTeamId } from './useEffectiveTeamId';
import { useAuth } from './useAuth';
import { useChannels } from './useChannels';
import { useTimezone } from '@/contexts/TimezoneContext';
import { invalidateReleaseState } from '@/lib/invalidateRelease';
import { MediaItem } from '@/types/posts';
import { scheduledInstancesService } from '@/services/scheduled-instances.service';
import { variationsService } from '@/services/variations.service';
import { approvalService } from '@/services/approval.service';
import { announceReleaseMoment } from '@/stores/releaseMoment.store';
import { announceRelease } from '@/stores/releaseUndo.store';
import { getReleaseFailureReason } from '@/utils/releaseFailureReason';
import type { ReleaseResult } from '@/types/approval';
import { postsApi, type DraftAutosavePayload, type CreateRequestLookup, type Post } from '@/services/posts';
import apiClient, { formatApiError, isGlobalGatingHandledError } from '@/services/api';
import type { PlatformOptions, ScheduledInstance } from '@/types/scheduled-instances';
import type { CalendarMappingData } from '@/stores/composerStore';
import { devLog } from '@/utils/logger';
import {
  shootWithRecovery,
  buildAlreadyScheduledMessage,
  classifySubmitWarning,
  type ShootOutcome,
} from '@/utils/shootRecovery';
import { isPostPublished, isPostFailed } from '@/utils/calendar';
import {
  SAVE_REQUEST_TIMEOUT_MS,
  CREATE_LOOKUP_DELAYS_MS,
  UPDATE_VERIFY_DELAYS_MS,
  ReplayedCreateError,
  SaveNotConfirmedError,
  generateCreateIntentKey,
  isTransientSaveError,
  isSaveInProgressConflict,
  isTimeoutError,
  isIdempotencyBodyMismatch,
  hashString,
  isNetworkError,
  isUuid,
  sleepUnlessCancelled,
  readPersistedIntentKey,
  persistIntentKey,
  clearPersistedIntentKey,
} from '@/utils/saveResilience';
import {
  checkCaptionWithinLimits,
  getYoutubeEffectiveCaptionText,
  CAPTION_TOO_LONG_ERROR_CODE,
} from '@/utils/captionLimit';

/**
 * Detect the transient "No team context" failure (HTTP 403, code: 'NO_TEAM')
 * surfaced by `limits.middleware.ts`. Agency-staff sessions can momentarily
 * lose team context (God Mode edge cases). This lets Save Draft degrade
 * gracefully — recover the session and ask the user to retry — instead of
 * showing a confusing raw error and risking the draft being perceived as lost.
 */
function isNoTeamError(error: unknown): boolean {
  return formatApiError(error).code === 'NO_TEAM';
}

/**
 * Human-friendly fallback for the backend's caption-too-long rejection, used only
 * if the descriptive message were ever missing (defensive — the backend currently
 * always includes one, see post.service.ts / scheduled-instance.service.ts).
 */
const CAPTION_TOO_LONG_FALLBACK =
  'Your caption is too long for one of the selected platforms. Please shorten it and try again.';

/**
 * If the API error is the backend's caption-too-long rejection (AppError code
 * 'caption_too_long'), return a clean, user-facing message. Returns null for any
 * other error so callers can fall through to their normal error extraction.
 */
function getCaptionTooLongMessage(error: unknown): string | null {
  const apiError = formatApiError(error);
  if (apiError.code !== CAPTION_TOO_LONG_ERROR_CODE) return null;
  return apiError.message || CAPTION_TOO_LONG_FALLBACK;
}

/**
 * usePostComposer Hook
 *
 * Unified hook for post composition (formerly usePostComposer).
 * Implements the "save first, then shoot" principle with a single handlePrimaryAction method.
 *
 * Supports 4 modes:
 * 1. fresh_create - New post, no context
 * 2. calendar_create - New post with date, map to calendar day
 * 3. edit_post - Edit existing post
 * 4. commit_mapped - Commit mapped post from calendar (shooting mode)
 *
 * Usage:
 * ```tsx
 * const composer = usePostComposer({
 *   autoSaveKey: 'post-draft',
 *   onSuccess: () => navigate('/posts'),
 *   shootingMappingId: 123, // For Mode 4
 * });
 *
 * // Primary action (rocket button)
 * await composer.handlePrimaryAction({
 *   rocketOn: true,
 *   shootMode: 'shoot_now',
 *   channelIds: [1, 2, 3],
 *   scheduledTime: new Date(),
 *   channelOptions: {}
 * });
 * ```
 */

interface UsePostComposerOptions {
  /** Called after the composer was reset because the effective brand changed while it was open (close the editor). */
  onTeamChanged?: () => void;
  /** Whether the owning editor is open. Reopening the same post must reload its baseline. */
  isOpen?: boolean;
  /**
   * localStorage key for auto-save
   */
  autoSaveKey?: string;

  /**
   * Enable auto-save (default: true)
   */
  enableAutoSave?: boolean;

  /**
   * Auto-save delay in ms (default: 30000 = 30s)
   */
  autoSaveDelay?: number;

  /**
   * Callback on successful post creation/update
   */
  onSuccess?: () => void;

  /**
   * Callback on error
   */
  onError?: (error: Error) => void;

  /**
   * Auto-restore draft on mount (default: true)
   * Note: Will be disabled when editing an existing post or in shooting mode
   */
  autoRestore?: boolean;

  /**
   * Post ID to edit (optional)
   * When provided, the composer will enter edit mode (Mode 3)
   */
  editPostId?: number;

  /**
   * Schedule mode - for scheduling existing posts to new channels
   * When true, channels are NOT pre-loaded from the existing post
   * (Posts can be scheduled multiple times to different channels)
   */
  isScheduleMode?: boolean;

  /**
   * Calendar Mapping ID to shoot (optional)
   * When provided, the composer enters shooting mode (Mode 4)
   */
  shootingMappingId?: number;
}

export interface ComposerDraft {
  caption: string;
  selectedChannels: number[];
  media: MediaItem[];
  scheduledTime: Date | null;
  labels: string[];
  calendarId: number | null;
  calendarNoteId: number | null;
  calendarHolidayId: number | null;
  isCarouselMode: boolean;
  /**
   * Create-intent key carried by a SERVER-side draft (set only by deserializeDraftFromServer). Never part
   * of the live composerDraft, so it cannot re-trigger the autosave hooks.
   */
  createIntentKey?: string | null;
}

/**
 * A saved draft is "effectively empty" when it has no caption text, no
 * media, no channels, and no schedule — i.e. nothing a user would be upset
 * to lose. Used to (a) stop autosave from ever overwriting a real draft
 * with an empty one (e.g. right after a form reset), and (b) decide
 * whether a saved draft is worth offering to restore.
 */
function isDraftEmpty(draft: ComposerDraft): boolean {
  return (
    !draft.caption?.trim() &&
    draft.media.length === 0 &&
    draft.selectedChannels.length === 0 &&
    !draft.scheduledTime
  );
}

/**
 * Drafts older than this are no longer offered for restore. The server mirror is
 * retained ~30 days (cleanup cron), so this frontend cap just stops nagging with
 * stale recovery prompts well before the row is purged. Tunable.
 */
// Keep in sync with backend UI_RESTORE_CAP_DAYS (postDraftAutosave.service.ts): the autosave cleanup cron relies on this cap.
const MAX_RESTORE_AGE_DAYS = 14;
const MAX_RESTORE_AGE_MS = MAX_RESTORE_AGE_DAYS * 24 * 60 * 60 * 1000;

/** A saved draft plus the time it was last written, for the restore prompt. */
export interface RestorableDraft {
  draft: ComposerDraft;
  savedAt: number | null;
}

/**
 * Convert a ComposerDraft into the JSON-safe wire payload sent to the
 * server-side draft-autosave endpoint. `Date` isn't valid JSON — encoded as
 * an ISO string here, reversed by `deserializeDraftFromServer`.
 */
/** Content hash of a draft (tolerant of a JSON-parsed scheduledTime string). Null when it can't be computed. */
function hashDraft(draft: ComposerDraft): string | null {
  try {
    return hashString(JSON.stringify(serializeDraftForServer(draft)));
  } catch (error) {
    devLog.warn('[PostComposer] could not hash draft', error);
    return null;
  }
}

function toIsoOrNull(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  try {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  } catch {
    return null;
  }
}

function serializeDraftForServer(draft: ComposerDraft): DraftAutosavePayload {
  return {
    caption: draft.caption,
    selectedChannels: draft.selectedChannels,
    media: draft.media,
    scheduledTime: toIsoOrNull(draft.scheduledTime),
    labels: draft.labels,
    calendarId: draft.calendarId,
    calendarNoteId: draft.calendarNoteId,
    calendarHolidayId: draft.calendarHolidayId,
    isCarouselMode: draft.isCarouselMode,
  };
}

/**
 * Parse a server-stored draft-autosave payload back into a ComposerDraft.
 * Defensive against a malformed/unexpected payload (never trust server JSON
 * blindly) — falls back to safe per-field defaults, mirroring the tolerance
 * `useAutosave` already applies when reading a legacy/corrupt localStorage
 * entry.
 */
function deserializeDraftFromServer(payload: DraftAutosavePayload): ComposerDraft {
  const parsedScheduledTime =
    typeof payload.scheduledTime === 'string' ? new Date(payload.scheduledTime) : null;

  return {
    caption: typeof payload.caption === 'string' ? payload.caption : '',
    selectedChannels: Array.isArray(payload.selectedChannels)
      ? payload.selectedChannels.filter((v): v is number => typeof v === 'number')
      : [],
    media: Array.isArray(payload.media)
      ? payload.media.filter((m): m is MediaItem => typeof m === 'object' && m !== null)
      : [],
    scheduledTime:
      parsedScheduledTime && !isNaN(parsedScheduledTime.getTime()) ? parsedScheduledTime : null,
    labels: Array.isArray(payload.labels)
      ? payload.labels.filter((v): v is string => typeof v === 'string')
      : [],
    calendarId: typeof payload.calendarId === 'number' ? payload.calendarId : null,
    calendarNoteId: typeof payload.calendarNoteId === 'number' ? payload.calendarNoteId : null,
    calendarHolidayId: typeof payload.calendarHolidayId === 'number' ? payload.calendarHolidayId : null,
    isCarouselMode: typeof payload.isCarouselMode === 'boolean' ? payload.isCarouselMode : false,
    createIntentKey: isUuid(payload.createIntentKey) ? payload.createIntentKey : null,
  };
}

/**
 * The create endpoint persists the post BEFORE it shoots. When the shoot then fails, the error body
 * carries `createdPostId` so the composer can adopt that post (a retry updates it instead of
 * creating a duplicate draft). Returns null for any other error shape.
 */
function getCreatedPostIdFromError(error: unknown): number | null {
  if (error instanceof ReplayedCreateError) return error.createdPostId;
  if (!axios.isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  if (typeof data !== 'object' || data === null) return null;
  const id = (data as Record<string, unknown>).createdPostId;
  return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
}

interface PrimaryActionParams {
  rocketOn: boolean;
  shootMode: 'shoot_now' | 'schedule';
  channelIds: number[];
  scheduledTime: Date | null;
  channelOptions: Record<number, PlatformOptions>;
}

/**
 * API Response types for V2 endpoints
 */
interface CreatePostV2Response {
  id: number;
  calendarMappings?: Array<{ id: number }>;
  /** Set on the result of a multi-post create: there is no single post to adopt or mark ready. */
  isMulti?: boolean;
}

type CreatePostBody = {
  caption: string;
  channelIds: number[];
  mediaIds: string[];
  mediaThumbnails?: Record<string, string>;
  status?: string;
  scheduledAt?: string;
  timezone?: string;
  labels: string[];
  calendarId: number | null;
  calendarNoteId: number | null;
  calendarHolidayId: number | null;
  isCarousel: boolean;
  channelOptions?: Record<number, PlatformOptions>;
  plannedDate?: string;
};

type UpdatePostParams = {
  id: number;
  data: {
    /** Omitted when scheduling a post whose content did not change (see getUnchangedContent) */
    caption?: string;
    channelIds?: number[];
    mediaIds?: string[];
    mediaThumbnails?: Record<string, string>;
    status?: string;
    scheduledAt?: string;
    timezone?: string;
    labels?: string[];
    calendarId?: number | null;
    calendarNoteId?: number | null;
    calendarHolidayId?: number | null;
    isCarousel?: boolean;
    channelOptions?: Record<number, PlatformOptions>;
  };
};

/** UI state of an unconfirmed save (timeout / dropped connection). */
export type SaveUiState = 'idle' | 'still_saving' | 'still_saving_keep_checking' | 'not_saved';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Interpret the stored body of a replayed create (lookup=completed). A success body yields the created
 * post; an error body (schedule step failed after the post committed) becomes a ReplayedCreateError.
 */
function readCreatedPayload(data: unknown): CreatedResult | null {
  const list: unknown[] = Array.isArray(data) ? data : [data];
  if (list.length === 0) return null;
  for (const item of list) {
    if (!isRecord(item) || typeof item.id !== 'number') return null;
  }
  return { post: list[0] as CreatePostV2Response, isMulti: Array.isArray(data) && list.length > 1 };
}

/** Returns null when the stored success body is malformed (treated as NOT confirmed). */
function parseReplayedCreate(
  body: unknown,
  outcome: 'created' | 'created_with_error' | undefined
): CreatedResult | null {
  if (outcome !== 'created_with_error' && isRecord(body) && body.success === true) {
    return readCreatedPayload(body.data);
  }
  const createdPostId =
    isRecord(body) && typeof body.createdPostId === 'number' && Number.isInteger(body.createdPostId)
      ? body.createdPostId
      : null;
  const message =
    isRecord(body) && typeof body.error === 'string'
      ? body.error
      : isRecord(body) && typeof body.message === 'string'
        ? body.message
        : '';
  const code = isRecord(body) && typeof body.code === 'string' ? body.code : null;
  throw new ReplayedCreateError(message, createdPostId, code);
}

/** Fields of a create body that an UPDATE accepts (status / plannedDate are create-only). */
function toUpdateData(body: CreatePostBody): UpdatePostParams['data'] {
  return {
    caption: body.caption,
    channelIds: body.channelIds,
    mediaIds: body.mediaIds,
    mediaThumbnails: body.mediaThumbnails,
    labels: body.labels,
    calendarId: body.calendarId,
    calendarNoteId: body.calendarNoteId,
    calendarHolidayId: body.calendarHolidayId,
    isCarousel: body.isCarousel,
    scheduledAt: body.scheduledAt,
    timezone: body.timezone,
    channelOptions: body.channelOptions,
  };
}

/**
 * Did the server-side post end up in the state we asked for? Verifies by INTENT, never by timestamps
 * (posts.updated_at is not a save proof). `knownMediaIds`: PostMedia ids that already belonged to the post
 * before the save; newly uploaded media (UUID-ish ids) can't be compared, so only the count is checked.
 */
function postMatchesSentUpdate(post: Post, sent: UpdatePostParams['data'], knownMediaIds: ReadonlySet<string>): boolean {
  const norm = (value: string): string => value.replace(/\r\n/g, '\n');
  if (sent.caption !== undefined && norm(post.caption ?? '') !== norm(sent.caption)) return false;

  if (sent.mediaIds !== undefined) {
    const serverMedia = new Set((post.postMedia ?? []).map((m) => String(m.id)));
    if ((post.postMedia ?? []).length !== sent.mediaIds.length) return false;
    for (const id of sent.mediaIds) {
      if (knownMediaIds.has(id) && !serverMedia.has(id)) return false;
    }
  }

  if (sent.channelIds !== undefined) {
    // Compare against the channels actually sent. Channels that already published are never removed by the
    // server (partial-publish edits), so extra PUBLISHED rows on the server are expected.
    const sentSet = new Set(sent.channelIds);
    const serverRows = post.postChannels ?? [];
    const serverSet = new Set(serverRows.map((pc) => pc.channelId));
    for (const id of sentSet) {
      if (!serverSet.has(id)) return false;
    }
    for (const pc of serverRows) {
      if (!sentSet.has(pc.channelId) && pc.status !== 'PUBLISHED' && !pc.externalPostId) return false;
    }
  }

  if (sent.scheduledAt) {
    if (!post.scheduledAt) return false;
    const delta = Math.abs(new Date(post.scheduledAt).getTime() - new Date(sent.scheduledAt).getTime());
    if (!(delta <= 60_000)) return false;
  }
  return true;
}

interface UpdatePostV2Response {
  id: number;
  calendarMappings?: Array<{ id: number }>;
}

/** A validated create payload. `isMulti`: a non-carousel multi-media create made N posts (no single id to adopt). */
interface CreatedResult {
  post: CreatePostV2Response;
  isMulti: boolean;
}

/**
 * Return type of usePostComposer hook
 * Use this to properly type the composer prop in child components
 */
export type UsePostComposerReturn = ReturnType<typeof usePostComposer>;

/** Which content parts of the form still equal the stored post (caption / labels compared like the server does). */
function getUnchangedContent(
  state: { caption: string; labels: string[]; media: Array<{ id: string }> },
  original: { caption?: string | null; labels?: string[] | null; postMedia?: Array<{ id: number | string }> | null }
): { caption: boolean; labels: boolean; media: boolean } {
  const text = (value: string | null | undefined): string => (value ?? '').replace(/\r\n/g, '\n').trim();
  const labelSet = (labels: readonly string[] | null | undefined): string =>
    JSON.stringify([...new Set((labels ?? []).map((l) => l.trim().toLowerCase()).filter(Boolean))].sort());
  const storedMedia = (original.postMedia ?? []).map((m) => String(m.id));
  const formMedia = state.media.map((m) => String(m.id));
  return {
    caption: text(state.caption) === text(original.caption),
    labels: labelSet(state.labels) === labelSet(original.labels),
    media: storedMedia.length === formMedia.length && storedMedia.every((id, index) => id === formMedia[index]),
  };
}

export function usePostComposer(options: UsePostComposerOptions = {}) {
  const {
    isOpen = true,
    onTeamChanged,
    autoSaveKey = 'post-draft-v2',
    enableAutoSave = true,
    autoSaveDelay = 30000,
    onSuccess,
    onError,
    autoRestore = true,
    editPostId,
    isScheduleMode = false,
    shootingMappingId,
  } = options;

  // Zustand store V2
  const store = useComposerStore();

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Get effective team ID for team-scoped autosave
  const { teamId, hasTeam } = useEffectiveTeamId();

  // Channels list — used to resolve selected channel IDs to platform keys for the
  // pre-submit caption length re-validation below. Shares the react-query cache
  // with the composer's own useChannels() call, so this is not an extra request.
  const { data: channels } = useChannels();

  // Used to recover from a transient "No team context" (NO_TEAM) 403 on save —
  // re-fetching /me re-asserts team context without discarding the typed draft.
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation('posts');

  // Get timezone from cascade (user > team > default: Europe/Berlin)
  const { timezone, formatDateTime } = useTimezone();

  // Check modes
  const isEditMode = !!editPostId;
  const isShootingMode = !!shootingMappingId;

  // Fetch post data when in edit mode
  const {
    data: existingPost,
    isLoading: isLoadingPost,
    error: loadPostError,
    refetch: refetchPost,
    isRefetching: isLoadPostRefetching,
    dataUpdatedAt: postUpdatedAt,
  } = usePost(editPostId || 0);

  // Freshness per open: the composer host stays mounted and usePost serves the cached row, so a reopened edit
  // composer would load a stale post (and capture a stale baseline). Every open stamps the time; the post is only
  // loaded into the form once data fetched AFTER that stamp has arrived (one refetch per open, see the effect below).
  const wasOpenRef = useRef(false);
  const openedAtRef = useRef(0);
  if (isOpen && !wasOpenRef.current) openedAtRef.current = Date.now();
  wasOpenRef.current = isOpen;
  const pendingMappingDateRef = useRef(false);
  const isPostFreshForOpen = !!existingPost && postUpdatedAt >= openedAtRef.current;
  useEffect(() => {
    if (!isOpen || !isEditMode) return;
    // With a cached row, restart a background fetch that is already in flight (its server read may predate this
    // open; the abort signal makes the old request really stop). Without cache, keep the first-time fetch usePost
    // already started. Either way exactly one effective GET per open.
    void refetchPost({ cancelRefetch: !!existingPost });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, editPostId]);

  // Fetch calendar mapping when in shooting mode
  const {
    data: mappingData,
    isLoading: isLoadingMapping,
    error: loadMappingError,
  } = useQuery<CalendarMappingData>({
    queryKey: ['calendar-mapping', shootingMappingId],
    queryFn: async () => {
      const response = await apiClient.get(`/api/calendar-mappings/${shootingMappingId}`);
      return response.data.data;
    },
    enabled: isShootingMode && !!shootingMappingId,
    staleTime: 0, // always fresh on open: scheduled time is seeded from plannedDate
  });

  // Live occurrence of THIS mapping, per mapping on purpose: the post-level displayStatus collapses all
  // occurrences, and an evergreen post can have a QUEUED sibling mapping while the one opened here is only
  // planned. QUEUED = scheduled (plain content edit); PICKED_UP = claimed by the worker, publishing now (read-only).
  // The first resolved answer of an open session is LATCHED: a later refetch (save invalidation, worker
  // transition) must never flip the mode under an open editor. No refetch on window focus for the same reason.
  const {
    data: mappingInstances,
    isFetching: isMappingInstancesFetching,
    isError: isMappingInstancesError,
    dataUpdatedAt: mappingInstancesUpdatedAt,
  } = useQuery<ScheduledInstance[]>({
    queryKey: ['calendar-mapping-instances', teamId, shootingMappingId],
    queryFn: () => scheduledInstancesService.listInstances({ calendarMappingId: shootingMappingId }),
    enabled: isOpen && isShootingMode && !!shootingMappingId,
    staleTime: 0,
    retry: false, // no auto-retry: on failure we fall back to today's flow, the backend guard stays authoritative
    refetchOnWindowFocus: false,
  });
  const scheduledInstanceLatchRef = useRef<{ key: string; resolved: boolean; instance: ScheduledInstance | null }>({
    key: '',
    resolved: false,
    instance: null,
  });
  const scheduledInstanceKey = `${openedAtRef.current}:${shootingMappingId ?? 0}`;
  if (!isOpen || !isShootingMode || scheduledInstanceLatchRef.current.key !== scheduledInstanceKey) {
    scheduledInstanceLatchRef.current = { key: isOpen && isShootingMode ? scheduledInstanceKey : '', resolved: false, instance: null };
  }
  const instanceLatch = scheduledInstanceLatchRef.current;
  if (isOpen && isShootingMode && !instanceLatch.resolved) {
    // Only an answer fetched AFTER this open counts (a cached list from an earlier session may be stale).
    const freshAnswer = !isMappingInstancesFetching && !!mappingInstances && mappingInstancesUpdatedAt >= openedAtRef.current;
    if (freshAnswer || isMappingInstancesError) {
      const live = (mappingInstances ?? []).filter((i) => i.status === 'QUEUED' || i.status === 'PICKED_UP');
      // A claimed (PICKED_UP) instance wins; otherwise the earliest scheduled one.
      const byTime = (x: ScheduledInstance, y: ScheduledInstance): number =>
        new Date(x.scheduledAt).getTime() - new Date(y.scheduledAt).getTime();
      const pickedUp = live.filter((i) => i.status === 'PICKED_UP').sort(byTime);
      const queued = live.filter((i) => i.status === 'QUEUED').sort(byTime);
      instanceLatch.instance = pickedUp[0] ?? queued[0] ?? null;
      instanceLatch.resolved = true;
    }
  }
  const liveScheduledInstance: ScheduledInstance | null = instanceLatch.instance;
  // First load only (never a background refetch): drives the Save-disabled reason.
  const isLoadingScheduledInstance: boolean = isOpen && isShootingMode && !!shootingMappingId && !instanceLatch.resolved;

  // Create post mutation (V2 API). Always sends the Idempotency-Key and a 30s per-request timeout
  // (the global axios default stays 10s).
  const createPostMutation = useMutation({
    mutationFn: async (params: { body: CreatePostBody; idempotencyKey: string }) => {
      const response = await apiClient.post<{ success: boolean; data: CreatePostV2Response }>(
        '/api/v2/posts',
        params.body,
        {
          timeout: SAVE_REQUEST_TIMEOUT_MS,
          headers: { 'Idempotency-Key': params.idempotencyKey },
        }
      );
      return response.data.data;
    },
  });

  // Update post mutation (V2 API). Idempotent by content, so no key; 30s per-request timeout.
  const updatePostMutation = useMutation({
    mutationFn: async (params: UpdatePostParams) => {
      const response = await apiClient.put<{ success: boolean; data: UpdatePostV2Response }>(
        `/api/v2/posts/${params.id}`,
        params.data,
        { timeout: SAVE_REQUEST_TIMEOUT_MS }
      );
      return response.data.data;
    },
  });

  // Shoot mutation (for Mode 4 - commit_mapped)
  const shootMutation = useMutation({
    mutationFn: async (data: {
      calendarMappingId: number;
      scheduledAt: Date;
      channelIds: number[];
      channelOptions?: Record<number, PlatformOptions>;
      immediate?: boolean;
    }) => {
      return scheduledInstancesService.shoot(data);
    },
  });

  // Auto-save hook - disabled in edit mode and shooting mode, team-scoped.
  // This is the SOLE local-draft writer for the composer (Zustand `persist`
  // was removed from composerStore — see composerStore.ts).
  // Off once a created post is adopted (editingPostId set): the 'new' draft is over.
  // Only a genuinely new post's content may be written under the 'new' draft key (draftOwner): content loaded for
  // editing, or left over from an edit that was just closed, is never autosaved as a new draft.
  const isAutoSaveActive =
    enableAutoSave &&
    !isEditMode &&
    !isShootingMode &&
    !store.editingPostId &&
    store.draftOwner === 'new' &&
    // Pinned at open: a draft typed for brand A is never written under brand B after a switch.
    store.draftTeamId !== null &&
    store.draftTeamId === teamId;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const teamIdRef = useRef(teamId);
  teamIdRef.current = teamId;

  // Composer context key for the server-side draft mirror: 'new' for a
  // brand-new post, or the post id when editing an existing one. The server
  // mirror below is currently only active for new posts (gated by
  // `isAutoSaveActive`), but this keeps the key correct if that's ever
  // extended to cover edit mode too.
  const draftContextKey = store.editingPostId ? String(store.editingPostId) : 'new';

  // Memoized so this object's identity is stable across renders unless the
  // actual draft content changes. Both autosave hooks below feed this into
  // `useDebounce(value, delay)`, whose effect deps are `[value, delay]` — a
  // fresh object literal here would reset BOTH debounce timers on every
  // render (including the 2s local autosave's own re-render heartbeat),
  // permanently starving the 8s server-side mirror debounce from ever
  // settling. See CLAUDE.md race-condition guidance.
  const composerDraft = useMemo<ComposerDraft>(() => ({
    caption: store.caption,
    selectedChannels: store.selectedChannels,
    media: store.media,
    scheduledTime: store.scheduledTime,
    labels: store.labels,
    calendarId: store.calendarId,
    calendarNoteId: store.calendarNoteId,
    calendarHolidayId: store.calendarHolidayId,
    isCarouselMode: store.isCarouselMode,
  }), [
    store.caption,
    store.selectedChannels,
    store.media,
    store.scheduledTime,
    store.labels,
    store.calendarId,
    store.calendarNoteId,
    store.calendarHolidayId,
    store.isCarouselMode,
  ]);

  const handleAutosaveSaved = useCallback(() => {
    devLog.log('[PostComposer] Draft auto-saved');
  }, []);

  const { restore, clear, hasSaved, flush, getSavedAt } = useAutosave<ComposerDraft>(composerDraft, {
    key: autoSaveKey,
    delay: autoSaveDelay,
    enabled: isAutoSaveActive,
    teamId,
    isEmpty: isDraftEmpty,
    onSave: handleAutosaveSaved,
  });

  // Server-side mirror of the local draft (debounced ~8s — longer than the
  // local 2s debounce), for cross-device recovery. Purely additive: never
  // blocks typing or the local autosave, and swallows/logs its own
  // failures — local storage remains the sole source of truth for
  // crash-safety (see useServerDraftAutosave's own doc comment).
  const { suppressCurrentDraft } = useServerDraftAutosave<ComposerDraft>(composerDraft, {
    contextKey: draftContextKey,
    delay: 8000,
    enabled: isAutoSaveActive && isOpen,
    isEmpty: isDraftEmpty,
    serialize: serializeDraftForServer,
    createIntentKey: store.createIntentKey,
    // Only a genuinely NEW, not-yet-saved post is mirrored: never once a created post is adopted.
    canWrite: () => {
      const state = useComposerStore.getState();
      return (
        isOpenRef.current &&
        !state.editingPostId &&
        state.draftOwner === 'new' &&
        state.draftTeamId !== null &&
        state.draftTeamId === teamIdRef.current
      );
    },
  });

  /**
   * Best-effort delete of the server-side draft mirror. Swallowed/logged —
   * must never surface an error or block the caller. Idempotent (a no-op if
   * no server row exists for this context, e.g. it was never mirrored, or
   * the server was down while typing).
   */
  const clearServerDraft = (): void => {
    postsApi.deleteDraftAutosave(draftContextKey).catch((error: unknown) => {
      devLog.error('[PostComposer] Failed to delete server draft autosave', error);
    });
  };

  /**
   * Clear the draft everywhere it's mirrored: local (synchronous,
   * crash-safety layer) AND the server-side cross-device copy. Use this
   * instead of the raw `clear()` from useAutosave at every point the draft
   * should be considered gone (successful save/publish, discard/start
   * fresh) — otherwise a stale server row would keep offering a restore
   * prompt on another device after the user already saved or discarded.
   */
  const clearDraftEverywhere = (): void => {
    clear();
    clearServerDraft();
    suppressCurrentDraft();
    // The draft is gone (saved / discarded): its create intent is over too.
    useComposerStore.getState().setCreateIntentKey(null);
    clearPersistedIntentKey(teamId);
  };

  // ---------------------------------------------------------------------------------------------
  // Save resilience (idempotent create + honest timeout UX). See .claude/save-idempotency/DESIGN.md.
  // ---------------------------------------------------------------------------------------------

  const [saveUiState, setSaveUiStateRaw] = useState<SaveUiState>('idle');
  const saveUiStateRef = useRef<SaveUiState>('idle');
  const setSaveUiState = (next: SaveUiState): void => {
    saveUiStateRef.current = next;
    setSaveUiStateRaw(next);
  };
  // Single synchronous busy guard shared by every save trigger (button, Ctrl/Cmd+S, all modes).
  const saveLockRef = useRef(false);
  const isMountedRef = useRef(true);
  // Bumped on reset / close / discard: a late poll or response from an older epoch must not adopt, toast or set state.
  const saveEpochRef = useRef(0);
  // Finishes a create that is only confirmed LATER (after the "keep checking" state: "Check again").
  const lateSuccessRef = useRef<((post: CreatePostV2Response) => Promise<void>) | null>(null);
  // The unconfirmed create that "Check again" resolves. `reconcileBody`: the key was already used for a
  // different body (422), so once the original post is found the CURRENT content is sent as a PUT.
  const pendingCreateRef = useRef<{ key: string; reconcileBody: CreatePostBody | null } | null>(null);
  const isStillSaving = (): boolean => saveUiStateRef.current === 'still_saving';
  const [isCheckingAgain, setIsCheckingAgain] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /** User-facing text for a failed save. Never the raw axios message ("timeout of 10000ms exceeded"). */
  const getSaveErrorMessage = (error: unknown, fallback?: string): string => {
    if (error instanceof ReplayedCreateError) {
      if (error.code === 'partial_create') return t('common:saving_partial_create');
      // Created-with-error but no post id to adopt: never trap the user in a replay loop, send them to the lists.
      if (error.createdPostId === null) return t('common:saving_check_posts');
      if (error.message) return error.message;
    }
    if (formatApiError(error).code === 'partial_create') return t('common:saving_partial_create');
    if (isTimeoutError(error)) return t('common:saving_timeout_friendly');
    if (isNetworkError(error)) return t('common:saving_network_error');
    const formatted = formatApiError(error);
    if (formatted.code === 'UNKNOWN_ERROR' || formatted.code === 'TIMEOUT' || formatted.code === 'NETWORK_ERROR') {
      return fallback ?? t('common:failed_save_post', { defaultValue: 'Failed to save post' });
    }
    return formatted.message || fallback || t('common:failed_save_post', { defaultValue: 'Failed to save post' });
  };

  /** Live composer content as a draft (store snapshot, independent of the render closure). */
  const snapshotDraft = (): ComposerDraft => {
    const s = useComposerStore.getState();
    return {
      caption: s.caption,
      selectedChannels: s.selectedChannels,
      media: s.media,
      scheduledTime: s.scheduledTime,
      labels: s.labels,
      calendarId: s.calendarId,
      calendarNoteId: s.calendarNoteId,
      calendarHolidayId: s.calendarHolidayId,
      isCarouselMode: s.isCarouselMode,
    };
  };

  /** Hash of the current draft content, pairing a persisted key with the exact draft it was issued for. */
  const hashCurrentDraft = (): string => hashDraft(snapshotDraft()) ?? '';

  /**
   * Return the create-intent key of the current NEW post, generating it lazily on the first create-save.
   * ONE key per create intent: kept across retries (never regenerated on not_found / failed). Persisted with
   * a hash of the draft so a reload mid-save keeps it. The server 'new' autosave slot learns the key through
   * the regular mirror (every 'new' autosave PUT carries it); nothing is awaited before the POST.
   */
  const ensureCreateIntentKey = (): string => {
    let key = useComposerStore.getState().createIntentKey;
    if (!key) {
      key = generateCreateIntentKey();
      useComposerStore.getState().setCreateIntentKey(key);
    }
    persistIntentKey(teamId, key, hashCurrentDraft());
    return key;
  };

  /** Drop the create-intent key (store + sessionStorage). */
  const retireCreateIntentKey = (): void => {
    useComposerStore.getState().setCreateIntentKey(null);
    clearPersistedIntentKey(teamId);
  };

  /**
   * A create is CONFIRMED (201, replay, or lookup=completed): adopt the created post so any further save
   * in this composer session is a PUT (never a second POST), and retire the create-intent key.
   */
  const adoptCreatedPost = (postId: number, calendarMappingId: number | null): void => {
    useComposerStore.setState({ editingPostId: postId, createIntentKey: null, createShootAt: null });
    if (calendarMappingId !== null) {
      useComposerStore.getState().setCalendarMappingId(calendarMappingId);
    }
    clearPersistedIntentKey(teamId);
  };

  const confirmCreated = ({ post, isMulti }: CreatedResult, keepBusy = false): CreatePostV2Response => {
    if (isMulti) {
      // N posts were made: there is no single post to adopt. Refresh the lists and end this intent.
      retireCreateIntentKey();
      invalidateReleaseState(queryClient, { teamId });
    } else {
      adoptCreatedPost(post.id, post.calendarMappings?.[0]?.id ?? null);
    }
    lateSuccessRef.current = null;
    pendingCreateRef.current = null;
    // keepBusy: a reconcile PUT follows, so there is no idle gap between the two steps.
    setSaveUiState(keepBusy ? 'still_saving' : 'idle');
    return isMulti ? { ...post, isMulti: true } : post;
  };

  /** Silent abort for a response / poll that arrives after the composer was reset or unmounted. */
  const abortStale = (): never => {
    invalidateReleaseState(queryClient, { teamId });
    throw new SaveNotConfirmedError('still_saving');
  };

  /**
   * Send the CURRENT content as an UPDATE of an already-created post (the user's latest intent, incl. the
   * schedule fields when rocketOn). Used when the key was already spent on a different body.
   */
  const reconcileAsUpdate = async (post: CreatePostV2Response, body: CreatePostBody): Promise<CreatePostV2Response> => {
    try {
      const updated = await updatePostResilient({ id: post.id, data: toUpdateData(body) });
      return {
        id: post.id,
        calendarMappings: updated.calendarMappings ?? post.calendarMappings,
      };
    } catch (error) {
      // confirmCreated kept the UI busy for this PUT: never leave it stuck on a definitive failure
      // (the post is adopted, so the user's next save is a plain update).
      if (!(error instanceof SaveNotConfirmedError)) setSaveUiState('idle');
      throw error;
    }
  };

  /**
   * Poll GET create-requests/:key on `delays` (ms). Returns the created post on `completed`; otherwise
   * sets the matching UI state and throws SaveNotConfirmedError (handled silently by the callers).
   */
  const resolveCreateByLookup = async (
    key: string,
    delays: readonly number[],
    isStale: () => boolean,
    reconcileBody: CreatePostBody | null
  ): Promise<CreatePostV2Response> => {
    setSaveUiState('still_saving');
    for (const delay of delays) {
      const alive = await sleepUnlessCancelled(delay, isStale);
      if (!alive) return abortStale();
      let lookup: CreateRequestLookup | null = null;
      try {
        lookup = await postsApi.getCreateRequest(key);
      } catch (lookupError) {
        // The lookup itself failed (still offline?): treat like in_progress and keep checking.
        devLog.warn('[PostComposer] create-request lookup failed, will retry', lookupError);
      }
      if (isStale()) return abortStale();
      if (lookup === null || lookup.status === 'in_progress') continue;
      if (lookup.status === 'completed') {
        let parsed: CreatedResult | null;
        try {
          parsed = parseReplayedCreate(lookup.response, lookup.outcome);
        } catch (replayError) {
          // created_with_error / partial_create: no success. Without a post id to adopt, end this intent
          // (lists are refreshed by the caller) so the user is never trapped in a replay loop.
          setSaveUiState('idle');
          pendingCreateRef.current = null;
          lateSuccessRef.current = null;
          if (replayError instanceof ReplayedCreateError && replayError.createdPostId === null) {
            retireCreateIntentKey();
          }
          throw replayError;
        }
        if (!parsed) continue; // malformed stored body: NOT confirmed, keep checking
        if (reconcileBody && parsed.isMulti) {
          // The key's original create made several posts: the current content can't be applied as one update.
          confirmCreated(parsed); // retires the key + refreshes the lists
          throw new ReplayedCreateError('', null, 'multi_post_mismatch');
        }
        const post = confirmCreated(parsed, reconcileBody !== null);
        if (reconcileBody) return reconcileAsUpdate(post, reconcileBody);
        return post;
      }
      // failed / not_found: nothing was saved.
      if (reconcileBody) {
        // The key is bound to a different body that never landed: it can never be used for this content.
        devLog.error('[PostComposer] idempotency body mismatch but the original create never landed; new key issued');
        retireCreateIntentKey();
      }
      pendingCreateRef.current = null;
      setSaveUiState('not_saved');
      toast.error(t('common:saving_not_saved_try_again'));
      throw new SaveNotConfirmedError('not_saved');
    }
    setSaveUiState('still_saving_keep_checking');
    throw new SaveNotConfirmedError('still_saving');
  };

  /**
   * Create a NEW post exactly once per save intent. Timeout / dropped connection / 502-504 / 409
   * save_in_progress are not errors: the outcome is looked up by the key instead. A 422
   * idempotency_body_mismatch means the key's original create had different content: find that post and
   * send the current content as an update.
   * `onLateSuccess` finishes the flow if the create is only confirmed later via "Check again".
   */
  const createPostResilient = async (
    body: CreatePostBody,
    onLateSuccess: (post: CreatePostV2Response) => Promise<void>
  ): Promise<CreatePostV2Response> => {
    const epoch = saveEpochRef.current;
    const isStale = (): boolean => epoch !== saveEpochRef.current || !isMountedRef.current;
    const key = ensureCreateIntentKey();
    setSaveUiState('idle');

    const unconfirmed = (reconcileBody: CreatePostBody | null): Promise<CreatePostV2Response> => {
      createPostMutation.reset(); // no red banner / raw message for an unconfirmed save
      lateSuccessRef.current = onLateSuccess;
      pendingCreateRef.current = { key, reconcileBody };
      return resolveCreateByLookup(key, CREATE_LOOKUP_DELAYS_MS, isStale, reconcileBody);
    };

    try {
      const data = await createPostMutation.mutateAsync({ body, idempotencyKey: key });
      if (isStale()) return abortStale();
      const created = readCreatedPayload(data);
      if (!created) return await unconfirmed(null); // malformed 201 body: confirm through the lookup
      return confirmCreated(created);
    } catch (error) {
      if (error instanceof SaveNotConfirmedError || error instanceof ReplayedCreateError) throw error;
      if (isStale()) return abortStale();
      if (isIdempotencyBodyMismatch(error)) return unconfirmed(body);
      if (!isSaveInProgressConflict(error) && !isTransientSaveError(error)) throw error;
      return unconfirmed(null);
    }
  };

  /**
   * Update an existing post. On timeout / dropped connection NOTHING is retried automatically: the post is
   * fetched and the save counts as applied ONLY if the server state equals what was sent (caption, channels,
   * media, schedule time). Verified by intent, never by timestamps. Anything else is "Not saved" and the user
   * decides about a retry.
   * TODO(B): once PUT-with-schedule is a MOVE of the live occurrence (DESIGN.md Part B) a repeat is idempotent
   * and the retry after a confirmed not-applied can be made automatic-safe.
   */
  const updatePostResilient = async (params: UpdatePostParams): Promise<UpdatePostV2Response> => {
    const epoch = saveEpochRef.current;
    const isStale = (): boolean => epoch !== saveEpochRef.current || !isMountedRef.current;
    // Stay busy through a reconcile that follows a create confirmation (no idle gap).
    if (saveUiStateRef.current !== 'still_saving') setSaveUiState('idle');

    const original = useComposerStore.getState().originalPost;
    const knownMediaIds = new Set<string>(
      original && original.id === params.id ? (original.postMedia ?? []).map((m) => String(m.id)) : []
    );

    try {
      const result = await updatePostMutation.mutateAsync(params);
      if (isStale()) return abortStale();
      return result;
    } catch (error) {
      if (error instanceof SaveNotConfirmedError) throw error;
      if (isStale()) return abortStale();
      if (!isTransientSaveError(error)) throw error;
      updatePostMutation.reset();
      setSaveUiState('still_saving');
      for (const delay of UPDATE_VERIFY_DELAYS_MS) {
        const alive = await sleepUnlessCancelled(delay, isStale);
        if (!alive) return abortStale();
        try {
          const post = await postsApi.getById(params.id);
          if (isStale()) return abortStale();
          if (postMatchesSentUpdate(post, params.data, knownMediaIds)) {
            setSaveUiState('idle');
            return {
              id: params.id,
              calendarMappings: post.calendarMappings?.map((m) => ({ id: m.id })),
            };
          }
        } catch (verifyError) {
          devLog.warn('[PostComposer] update verification failed, will retry', verifyError);
        }
      }
      setSaveUiState('not_saved');
      toast.error(t('common:saving_not_saved_try_again'));
      throw new SaveNotConfirmedError('not_saved');
    }
  };

  /** Run a save flow under the single busy guard. A second trigger while one runs (or while unconfirmed) is ignored. */
  const runExclusiveSave = async (flow: () => Promise<void>): Promise<void> => {
    if (saveLockRef.current || isCheckingAgain) return;
    if (saveUiStateRef.current === 'still_saving' || saveUiStateRef.current === 'still_saving_keep_checking') return;
    saveLockRef.current = true;
    try {
      await flow();
    } finally {
      saveLockRef.current = false;
      // Safety net: no poll is active once the flow ended, so a lingering 'still_saving' is stuck state.
      if (isStillSaving() && isMountedRef.current) setSaveUiState('idle');
    }
  };

  /**
   * The create persisted the post BEFORE a later step failed (error body carries `createdPostId`, directly
   * or through a replayed lookup): adopt it so the next submit is an UPDATE (no duplicate draft).
   * Without a post id to adopt (partial_create / created_with_error): refresh the lists and DROP the key, so
   * the user is told to check their posts and is never trapped in a replay loop.
   */
  const adoptFromCreateError = (error: unknown): void => {
    const createdPostId = getCreatedPostIdFromError(error);
    if (createdPostId === null) {
      const replayedWithoutId = error instanceof ReplayedCreateError;
      const code = error instanceof ReplayedCreateError ? error.code : formatApiError(error).code;
      if (code === 'partial_create' || replayedWithoutId) {
        retireCreateIntentKey();
        invalidateReleaseState(queryClient, { teamId });
      }
      return;
    }
    if (useComposerStore.getState().editingPostId !== createdPostId) {
      useComposerStore.setState({ editingPostId: createdPostId });
      // The 'new' autosave context is now superseded by the post id: drop its server mirror so another
      // device does not offer to restore a duplicate of the post we just created (best-effort).
      postsApi.deleteDraftAutosave('new').catch((draftError: unknown) => {
        devLog.error('[PostComposer] Failed to delete server draft autosave after adoption', draftError);
      });
    }
    retireCreateIntentKey();
    // Posts list + calendar grid (+ release board) so the freshly created post shows up.
    invalidateReleaseState(queryClient, { teamId, postId: createdPostId });
  };

  /** Same failure handling as the normal save paths (used by "Check again" as well). */
  const handleSaveFailure = (error: unknown, fallback: string): void => {
    if (error instanceof SaveNotConfirmedError) return;
    adoptFromCreateError(error);
    if (isGlobalGatingHandledError(error)) {
      // A global upsell/modal already explains it: no second toast, but callers still learn the save failed.
      onError?.(error instanceof Error ? error : new Error(fallback));
      return;
    }
    const submitWarning = classifySubmitWarning(error, t, formatDateTime);
    if (submitWarning) {
      toast.warning(submitWarning.message);
      onError?.(new Error(submitWarning.message));
      return;
    }
    const message = getCaptionTooLongMessage(error) || getSaveErrorMessage(error, fallback);
    toast.error(message);
    onError?.(error instanceof Error ? error : new Error(message));
  };

  /** "Check again" after the keep-checking state: one immediate lookup, no new POST. */
  const checkSaveAgain = async (): Promise<void> => {
    const pending = pendingCreateRef.current;
    if (!pending || saveLockRef.current || isCheckingAgain) return;
    const epoch = saveEpochRef.current;
    const isStale = (): boolean => epoch !== saveEpochRef.current || !isMountedRef.current;
    // Captured BEFORE the lookup: confirmCreated clears the ref once the create is confirmed.
    const finish = lateSuccessRef.current;
    saveLockRef.current = true;
    setIsCheckingAgain(true);
    try {
      const post = await resolveCreateByLookup(pending.key, [0], isStale, pending.reconcileBody);
      if (finish) await finish(post);
    } catch (error) {
      handleSaveFailure(error, 'Failed to save post');
    } finally {
      saveLockRef.current = false;
      if (isMountedRef.current) {
        setIsCheckingAgain(false);
        // Same safety net as runExclusiveSave: the lookup is over, 'still_saving' can only be stuck state.
        if (isStillSaving()) setSaveUiState('idle');
      }
    }
  };

  /**
   * Flush the in-progress (non-debounced) draft to localStorage on lifecycle
   * events that precede a freeze/close/reload, so content typed within the
   * debounce window isn't lost. `flush()` itself no-ops on an empty draft.
   */
  useEffect(() => {
    if (!isAutoSaveActive) return undefined;

    const handleVisibilityChange = () => {
      if (document.hidden) flush();
    };
    const handlePageHide = () => flush();
    const handleBeforeUnload = () => flush();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAutoSaveActive, flush]);

  /**
   * Load existing post when editing
   */
  useEffect(() => {
    if (isOpen && isEditMode && existingPost && isPostFreshForOpen) {
      // A post of another brand than the effective one (brand switched meanwhile, stale link): never load it for
      // editing, the server would let a creator save it into the wrong context. Close with the brand-changed note.
      if (existingPost.teamId !== undefined && teamId !== null && existingPost.teamId !== teamId) {
        toast.info(t('common:composer_closed_brand_changed'));
        onTeamChanged?.();
        return;
      }
      // Load the post into the store
      store.loadPost(existingPost);
      devLog.log('[PostComposer] Loaded existing post for editing:', existingPost.id);

      // In schedule mode, clear channels - user should select fresh channels for new scheduling
      if (isScheduleMode) {
        store.setChannels([]);
        store.setScheduledTime(null);
        devLog.log('[PostComposer] Schedule mode: cleared channels and time for fresh selection');
      }

      // Calendar-mapped post: loadPost above resets scheduledTime to post.scheduledAt (null for a mapped post
      // with no scheduled instance), wiping the planned date loadFromMapping may already have seeded from the
      // cached mapping. Re-derive it here so the result never depends on which of post / mapping resolved first.
      // A real post.scheduledAt still wins. Runs once per open (same deps as this effect), so later user edits
      // in the picker are never overwritten by a refetch.
      if (isShootingMode && !existingPost.scheduledAt) {
        if (mappingData?.plannedDate && mappingData.post?.id === existingPost.id) {
          store.setScheduledTime(new Date(mappingData.plannedDate));
          pendingMappingDateRef.current = false;
        } else {
          // Mapping not resolved yet: apply its planned date the moment it arrives (effect below).
          pendingMappingDateRef.current = true;
        }
      } else {
        pendingMappingDateRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, existingPost?.id, isScheduleMode, isPostFreshForOpen]);

  /**
   * Mapping resolved AFTER the post was loaded into the form: seed the planned date once, only while the picker
   * is still empty (never over a user's pick or a real post.scheduledAt).
   */
  useEffect(() => {
    if (!isOpen) {
      pendingMappingDateRef.current = false;
      return;
    }
    if (!pendingMappingDateRef.current || !isShootingMode || !mappingData?.plannedDate) return;
    // Only the mapping of the post being edited may seed the date.
    if (mappingData.post?.id !== editPostId) return;
    pendingMappingDateRef.current = false;
    if (!useComposerStore.getState().scheduledTime) {
      useComposerStore.getState().setScheduledTime(new Date(mappingData.plannedDate));
    }
  }, [isOpen, isShootingMode, editPostId, mappingData?.plannedDate, mappingData?.post?.id]);

  /**
   * Load mapping into store when in shooting mode
   */
  useEffect(() => {
    if (isShootingMode && mappingData && !store.calendarMappingId) {
      store.loadFromMapping(mappingData);
      devLog.log('[PostComposer] Loaded mapping data for shooting:', mappingData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShootingMode, mappingData]);

  /**
   * Apply a previously-saved draft to the store. Does NOT read from
   * localStorage itself — callers get the draft via `getRestorableDraft()`
   * first (so the caller can show a confirmation prompt before restoring).
   */
  const applyDraftToStore = (draft: ComposerDraft): void => {
    store.setCaption(draft.caption);
    store.setChannels(draft.selectedChannels);
    store.setMedia(draft.media);
    // A locally restored draft carries a JSON-parsed string: normalise to a real Date (or null).
    const restoredTime = draft.scheduledTime ? new Date(draft.scheduledTime) : null;
    store.setScheduledTime(restoredTime && !Number.isNaN(restoredTime.getTime()) ? restoredTime : null);
    store.setLabels(draft.labels || []);
    store.setCalendarId(draft.calendarId || null);
    store.setCalendarEvent(draft.calendarNoteId ?? null, draft.calendarHolidayId ?? null);
    store.setCarouselMode(draft.isCarouselMode ?? false);
    // Re-attach the create intent of an unconfirmed save: if that create DID land, the retry replays it
    // instead of making a duplicate.
    try {
      const draftHash = hashDraft(draft);
      const restoredKey = isUuid(draft.createIntentKey)
        ? draft.createIntentKey
        : draftHash !== null
          ? readPersistedIntentKey(teamId, draftHash)
          : null;
      if (restoredKey) store.setCreateIntentKey(restoredKey);
    } catch (error) {
      devLog.warn('[PostComposer] could not re-attach the create intent key (ignored)', error);
    }
    devLog.log('[PostComposer] Draft restored into store');
  };

  /**
   * Look up a genuinely-restorable draft (non-empty, and only for new posts
   * — never in edit or shooting mode, where the composer is seeded from an
   * existing post/mapping instead). Considers BOTH the local draft
   * (synchronous, useAutosave/localStorage) and the server-side mirror
   * (async, cross-device) and returns the NEWER of the two by `savedAt`; if
   * only one side has a genuine (non-empty) draft, that one is used. Returns
   * null when there's nothing worth offering to restore on either side.
   *
   * The server fetch is best-effort: a network failure or a down backend
   * degrades gracefully to "local only" rather than throwing, so this can
   * never block opening the composer. Callers (PostCreator) use this to show
   * a single explicit "Restore draft?" prompt instead of silently
   * repopulating the form.
   */
  const getRestorableDraft = async (): Promise<RestorableDraft | null> => {
    if (isEditMode || isShootingMode || !autoRestore) return null;

    let localCandidate: RestorableDraft | null = null;
    if (hasSaved()) {
      const draft = restore();
      if (draft && !isDraftEmpty(draft)) {
        localCandidate = { draft, savedAt: getSavedAt() };
      }
    }

    let serverCandidate: RestorableDraft | null = null;
    try {
      const response = await postsApi.getDraftAutosave(draftContextKey);
      if (response.success && response.data) {
        const draft = deserializeDraftFromServer(response.data.payload);
        if (!isDraftEmpty(draft)) {
          serverCandidate = { draft, savedAt: new Date(response.data.savedAt).getTime() };
        }
      }
    } catch (error) {
      devLog.error(
        '[PostComposer] Failed to fetch server draft autosave (falling back to local only)',
        error
      );
    }

    let chosen: RestorableDraft | null;
    if (localCandidate && serverCandidate) {
      const localSavedAt = localCandidate.savedAt ?? 0;
      const serverSavedAt = serverCandidate.savedAt ?? 0;
      chosen = serverSavedAt > localSavedAt ? serverCandidate : localCandidate;
    } else {
      chosen = localCandidate || serverCandidate;
    }

    if (chosen && chosen.savedAt !== null && Date.now() - chosen.savedAt > MAX_RESTORE_AGE_MS) {
      return null; // too old to be a useful recovery — don't prompt
    }
    return chosen;
  };

  /**
   * Clear edit mode on unmount
   */
  useEffect(() => {
    return () => {
      if (isEditMode) {
        // Leaving edit mode: only the draft owner must change NOW, so no autosave can write the edited post's
        // content under the 'new' key. The content itself stays until PostCreator's 300 ms close reset (no empty
        // form during the exit animation); a fast create-mode reopen is covered by the guard effect below.
        useComposerStore.setState({ draftOwner: null });
        store.clearEditMode();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  // Pin the brand of a new draft when the create composer opens (after a reset it is pinned again).
  useEffect(() => {
    if (!isOpen || teamId === null) return;
    const state = useComposerStore.getState();
    if (state.draftOwner === 'new' && state.draftTeamId === null) {
      useComposerStore.setState({ draftTeamId: teamId });
    }
  }, [isOpen, teamId, store.draftOwner, store.draftTeamId]);

  // The effective brand changed while the editor is open (any instance, global host or page mount): its content
  // belongs to the previous brand, so reset it and close instead of letting it save or autosave into the new one.
  const prevTeamIdRef = useRef(teamId);
  useEffect(() => {
    const previous = prevTeamIdRef.current;
    prevTeamIdRef.current = teamId;
    if (previous === null || previous === teamId || !isOpen) return;
    useComposerStore.getState().reset();
    toast.info(t('common:composer_closed_brand_changed'));
    onTeamChanged?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  /**
   * A create-mode open must start from a clean form: if the store still holds an edited post's (or a mapped
   * item's) content (e.g. a very fast reopen), reset it before anything can read or autosave it.
   */
  useEffect(() => {
    if (!isOpen || isEditMode || isShootingMode) return;
    if (useComposerStore.getState().draftOwner !== 'new') useComposerStore.getState().reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, isShootingMode]);

  /**
   * Default shoot mode based on planned date
   * If plannedDate is in the past → shoot_now, future → schedule
   */
  const defaultShootMode = useMemo<'shoot_now' | 'schedule'>(() => {
    if (!mappingData?.plannedDate) return 'schedule';
    const planned = new Date(mappingData.plannedDate);
    const now = new Date();
    return planned <= now ? 'shoot_now' : 'schedule';
  }, [mappingData?.plannedDate]);

  /**
   * Validation for publishing (requires content + channels)
   */
  const validate = (): { valid: boolean; error?: string } => {
    // Must have either caption or media
    const hasContent = store.caption.trim() || store.media.length > 0;

    if (!hasContent) {
      return { valid: false, error: 'Please add a caption or media' };
    }

    if (store.selectedChannels.length === 0) {
      return { valid: false, error: 'Please select at least one channel' };
    }

    if (store.scheduledTime && store.scheduledTime < new Date()) {
      return {
        valid: false,
        error: 'Scheduled time cannot be in the past',
      };
    }

    return { valid: true };
  };

  /**
   * Validation for drafts (only requires content, no channels needed)
   */
  const validateDraft = (): { valid: boolean; error?: string } => {
    // Must have either caption or media
    const hasContent = store.caption.trim() || store.media.length > 0;

    if (!hasContent) {
      return { valid: false, error: 'Please add a caption or media to save a draft' };
    }

    return { valid: true };
  };

  /**
   * Can submit form (for publishing)
   */
  const canSubmit = (): boolean => {
    return validate().valid;
  };

  /**
   * Can save as draft (less restrictive)
   */
  const canSaveDraft = (): boolean => {
    return validateDraft().valid;
  };

  /**
   * Resolve channel IDs to their lowercased platform keys (e.g. 'linkedin',
   * 'google_my_business'), for platform-limit checks. Mirrors the
   * selectedPlatforms() helper in PostCreator/index.tsx exactly (same fallback
   * to 'linkedin' when no channels are resolvable) so the pre-submit caption
   * check below evaluates the same platform set the disabled-button guard did.
   */
  const getPlatformsForChannelIds = (channelIds: number[]): string[] => {
    if (channelIds.length === 0) return ['linkedin'];
    const activeChannels = channels?.filter((ch) => channelIds.includes(ch.id));
    if (!activeChannels || activeChannels.length === 0) return ['linkedin'];
    return [...new Set(activeChannels.map((ch) => ch.platform.toLowerCase()))];
  };

  /**
   * Primary Action Handler - Unified save + optional shoot
   *
   * Implements "save first, then shoot" principle:
   * - Step 1: Save content (to variation or post)
   * - Step 2: Shoot if rocketOn is true
   */
  const handlePrimaryAction = (params: PrimaryActionParams): Promise<void> =>
    runExclusiveSave(() => runPrimaryAction(params));

  const runPrimaryAction = async (params: PrimaryActionParams): Promise<void> => {
    const { rocketOn, shootMode, channelIds, scheduledTime, channelOptions } = params;

    // Belt-and-suspenders re-validation: the rocket/publish CTA is already disabled
    // while the caption exceeds a selected platform's limit (see getDisabledReason()
    // in PostCreator/VariationCreator), but that's a UI guard only. Re-run the
    // identical check here so a bypassed/disabled button, race, or keyboard
    // shortcut can never submit an over-limit caption to the API. Only applies
    // when actually scheduling/shooting (rocketOn) — draft saves are unrestricted,
    // matching both the UI guard and the backend (which only validates caption
    // length when scheduledAt is present).
    if (rocketOn && store.caption.trim()) {
      const platforms = getPlatformsForChannelIds(channelIds);
      // YouTube publishes its per-channel description override (if set) instead of the
      // caption — validate that, not the caption, so a long caption + short YouTube
      // description isn't wrongly blocked (and vice versa).
      const youtubeText = getYoutubeEffectiveCaptionText(store.caption, channelIds, channelOptions, channels);
      const captionCheck = checkCaptionWithinLimits(store.caption, platforms, { youtube: youtubeText });
      if (!captionCheck.isWithinAll) {
        const message = t('caption_over_limit', {
          defaultValue: 'Caption too long for: {{platforms}}',
          platforms: captionCheck.overPlatformNames,
        });
        toast.error(message);
        onError?.(new Error(message));
        return;
      }
    }

    // STEP 3 (shared by the normal path and a create confirmed later via "Check again").
    const completeSuccess = (shootOutcome: ShootOutcome, liveScheduledAt: string | null): void => {
      clearDraftEverywhere(); // Clear auto-save draft (local + server)

      void queryClient.invalidateQueries({ queryKey: ['calendar-mapping-instances'] });
      // Invalidate queries
      invalidateReleaseState(queryClient, {
        teamId,
        postId: useComposerStore.getState().editingPostId ?? undefined,
      });

      const successMsg = rocketOn
        ? shootMode === 'shoot_now'
          ? 'Post scheduled for immediate publishing!'
          : 'Post scheduled successfully!'
        : t('common:saving_saved');
      if (shootOutcome === 'already_scheduled') {
        toast.info(buildAlreadyScheduledMessage(liveScheduledAt, formatDateTime));
      } else {
        toast.success(successMsg);
      }

      setSaveUiState('idle');
      onSuccess?.();
    };

    try {
      // STEP 1: SAVE
      let savedPostId: number | null = store.editingPostId;
      let savedCalendarMappingId: number | null = store.calendarMappingId;

      // Convert changedThumbnails Map to object for API
      const mediaThumbnails =
        store.changedThumbnails.size > 0 ? Object.fromEntries(store.changedThumbnails) : undefined;

      // Mode 4 with variation: Save to variation
      if (store.saveTarget === 'variation' && store.variationId && store.editingPostId) {
        await variationsService.updateVariation(store.editingPostId, store.variationId, {
          caption: store.caption,
          labels: store.labels,
        });
        // Also update variation media if changed
        await variationsService.updateVariationMedia(store.editingPostId, store.variationId, {
          mediaIds: store.media.map((m) => parseInt(m.id, 10)),
        });
        invalidateReleaseState(queryClient, {
          teamId,
          postId: store.editingPostId,
          variationId: store.variationId,
        });
        devLog.log('[PostComposer] Updated variation:', store.variationId);
      }
      // Mode 3 (edit) or Mode 4 without variation: Update post
      else if (store.editingPostId) {
        // For Mode 1/2/3 with rocketOn: include scheduledAt + channelIds (backend creates mapping + shoots)
        const shouldIncludeSchedule = rocketOn && !isShootingMode;
        // A FULLY published post (no failed leg) edited in place (rocket OFF) is a
        // content-only, evergreen edit — never re-publish or touch channels. The
        // backend DELETEs + RECREATEs post_channels whenever channelIds is sent,
        // which would destroy the published per-channel record. Only send
        // channelIds when actually (re)scheduling/shooting. Uses inferred publish
        // state (status OR instancesSummary OR calendarMappings.status), not raw
        // post.status, so it also protects scheduled-path-published (desynced)
        // posts. Gated with !isPostFailed: a PARTIAL post (some channels published,
        // some failed) still reports isPostPublished=true, but the user must be
        // able to edit/retry the failed leg's channels, so we exclude it here and
        // let channelIds through — the backend's per-row guard (only touches
        // PostChannel rows with platformPostId=null) still protects the
        // already-published rows from being wiped.
        // Prefer `displayStatus` -- the backend's collapse of the post's LATEST
        // non-cancelled occurrence -- over the legacy isPostPublished/isPostFailed
        // helpers, which read the UNSCOPED calendarMappings/instancesSummary (all
        // occurrences, evergreen). An evergreen post with an old PUBLISHED
        // occurrence but a newer SCHEDULED one would otherwise be misread as
        // "published" here. Fall back to the legacy check only when the backend
        // hasn't populated displayStatus (e.g. some minimal fetch paths).
        const isPublishedEdit = store.originalPost
          ? store.originalPost.displayStatus !== undefined
            ? store.originalPost.displayStatus === 'PUBLISHED'
            : isPostPublished(store.originalPost) && !isPostFailed(store.originalPost)
          : false;
        // Scheduling a post whose content did not change (typically a RELEASED, content-frozen one: the rocket
        // flow) must send ONLY the scheduling payload. Re-sending the content, even identical, risks a false
        // "content changed" on the server (e.g. isCarousel derived from the media count vs the stored type) and
        // a denied save on frozen content. A real content edit still sends everything.
        const unchanged = shouldIncludeSchedule && store.originalPost ? getUnchangedContent(store, store.originalPost) : null;
        const updateData = {
          caption: unchanged?.caption ? undefined : store.caption,
          channelIds: isShootingMode
            ? undefined
            : shouldIncludeSchedule
              ? channelIds
              : isPublishedEdit
                ? undefined // content-in-place edit of a published post: don't resend channels (backend would wipe/recreate post_channels)
                : store.selectedChannels,
          mediaIds: unchanged?.media ? undefined : store.media.map((m) => m.id),
          mediaThumbnails,
          labels: unchanged?.labels ? undefined : store.labels,
          calendarId: isShootingMode ? undefined : store.calendarId,
          calendarNoteId: isShootingMode ? undefined : store.calendarNoteId,
          calendarHolidayId: isShootingMode ? undefined : store.calendarHolidayId,
          // Derived from the media list: sent only when the media changed or the user flipped the toggle.
          isCarousel: unchanged?.media && !store.carouselToggledByUser ? undefined : store.isCarouselMode,
          ...(shouldIncludeSchedule && {
            scheduledAt: (shootMode === 'shoot_now' ? new Date() : scheduledTime)?.toISOString(),
            timezone: timezone,
            channelOptions: Object.keys(channelOptions).length > 0 ? channelOptions : undefined,
          }),
        };

        devLog.log('[PostComposer] UPDATE caption being sent:', {
          length: store.caption?.length,
          newlines: (store.caption?.match(/\n/g) || []).length,
          first80: JSON.stringify((store.caption || '').substring(0, 80)),
        });

        const response = await updatePostResilient({
          id: store.editingPostId,
          data: updateData,
        });
        savedPostId = store.editingPostId;
        savedCalendarMappingId = isShootingMode
          ? store.calendarMappingId  // Mode 4: store has the correct mapping from loadFromMapping
          : (response.calendarMappings?.[0]?.id || store.calendarMappingId);
        devLog.log('[PostComposer] Updated post:', savedPostId);
      }
      // Mode 1 or 2: Create new post
      else {
        // For Mode 1/2 with rocketOn: include scheduledAt + channelIds (backend creates mapping + shoots)
        const shouldIncludeSchedule = rocketOn;
        // shoot_now: freeze the timestamp per create intent, so a retry of the same intent sends the identical
        // body (the server hashes it; a fresh `new Date()` per click would 422). Cleared with the key.
        // Kept ONLY while an unconfirmed create is pending; after a definitive failure the next click takes a
        // fresh time (a stale past scheduledAt would be rejected; the failed row accepts a new body).
        const frozenShootNowAt =
          (pendingCreateRef.current ? useComposerStore.getState().createShootAt : null) ?? new Date().toISOString();
        if (shouldIncludeSchedule && shootMode === 'shoot_now') {
          useComposerStore.getState().setCreateShootAt(frozenShootNowAt);
        }
        const createData = {
          caption: store.caption,
          channelIds: shouldIncludeSchedule ? channelIds : store.selectedChannels,
          mediaIds: store.media.map((m) => m.id),
          mediaThumbnails,
          status: 'UNSCHEDULED' as const,
          labels: store.labels,
          calendarId: store.calendarId,
          calendarNoteId: store.calendarNoteId,
          calendarHolidayId: store.calendarHolidayId,
          isCarousel: store.isCarouselMode,
          plannedDate: store.scheduledTime?.toISOString(),
          ...(shouldIncludeSchedule && {
            scheduledAt: shootMode === 'shoot_now' ? frozenShootNowAt : scheduledTime?.toISOString(),
            timezone: timezone,
            channelOptions: Object.keys(channelOptions).length > 0 ? channelOptions : undefined,
          }),
        };

        // The create is confirmed (201 / replay / lookup) when this returns; the composer has then adopted
        // the post (editingPostId + calendar mapping), so any further save is an UPDATE.
        const response = await createPostResilient(createData, async () => {
          completeSuccess('shot', null);
        });
        savedPostId = response.id;
        savedCalendarMappingId = response.calendarMappings?.[0]?.id || null;
        devLog.log('[PostComposer] Created post:', savedPostId);
      }

      // STEP 2: SHOOT (only for Mode 4 with rocketOn, OR if mapping exists but not already scheduled)
      // For Mode 1/2/3, the backend already handled scheduling if we passed scheduledAt
      let shootOutcome: ShootOutcome = 'shot';
      let liveScheduledAt: string | null = null;
      if (rocketOn && isShootingMode && savedCalendarMappingId) {
        const isImmediate = shootMode === 'shoot_now';
        const shootTime = isImmediate ? new Date() : scheduledTime;

        if (shootTime) {
          const shootResult = await shootWithRecovery(
            {
              calendarMappingId: savedCalendarMappingId,
              scheduledAt: shootTime,
              channelIds,
              channelOptions: Object.keys(channelOptions).length > 0 ? channelOptions : undefined,
              immediate: isImmediate,
            },
            shootMutation.mutateAsync
          );
          shootOutcome = shootResult.outcome;
          liveScheduledAt = shootResult.liveScheduledAt;
          devLog.log('[PostComposer] Shot scheduled instance for mapping:', savedCalendarMappingId);
        }
      }

      // STEP 3: Clean up
      completeSuccess(shootOutcome, liveScheduledAt);
    } catch (error) {
      // Already surfaced through the save-resilience UI state (still saving / not saved): stay silent.
      if (error instanceof SaveNotConfirmedError) return;
      // Create succeeded but the follow-up shoot failed: adopt the created post so the next submit is
      // an UPDATE of it (no duplicate draft). Fields, toasts and the error flow below stay untouched.
      adoptFromCreateError(error);
      // A network-dropped shoot that we could not confirm: soft, non-alarming
      // prompt to verify/reload rather than a hard failure (the write may have committed).
      // Unconfirmed shoot / same-day already_scheduled 409: amber warning, NOT a red error.
      // Nothing is reset, so the user keeps their draft, time and channels.
      const submitWarning = classifySubmitWarning(error, t, formatDateTime);
      if (submitWarning) {
        shootMutation.reset(); // clear the failed shoot mutation so no red error banner shows (amber toast only)
        toast.warning(submitWarning.message);
        if (submitWarning.kind === 'conflict') onError?.(new Error(submitWarning.message));
        return;
      }
      // Surface the backend's caption-too-long rejection clearly (e.g. a race that
      // slipped past the guard above, or a caption edited after the guard ran but
      // before the request completed). Falls back to normal error extraction for
      // every other error.
      // Scheduling hit the content freeze (released / in review): say what to do instead of the generic frozen text.
      const frozenWhileScheduling = rocketOn && (formatApiError(error).code ?? '').startsWith('release.frozen');
      const message = frozenWhileScheduling
        ? t('common:release_schedule_frozen_hint')
        : getCaptionTooLongMessage(error) || getSaveErrorMessage(error, 'Failed to process post');
      if (!isGlobalGatingHandledError(error)) toast.error(message);
      onError?.(error instanceof Error ? error : new Error(message));
    }
  };

  /**
   * Save as draft (uses less restrictive validation - no channels required)
   */
  const handleSaveDraft = (): Promise<void> => runExclusiveSave(runSaveDraft);

  const runSaveDraft = async (): Promise<void> => {
    const validation = validateDraft();
    if (!validation.valid) {
      toast.error(validation.error || 'Validation failed');
      onError?.(new Error(validation.error));
      return;
    }

    // Proactive guard: agency-staff sessions can momentarily lose team context
    // (God Mode edge cases). Short-circuit before firing a request we know
    // will 403 — the draft stays exactly as typed, nothing is discarded.
    if (!hasTeam) {
      devLog.warn('[PostComposer] Save Draft blocked: no effective team context');
      try {
        const refreshed = await refreshUser();
        const stillNoTeam =
          !refreshed.godModeSession && !refreshed.teamId && !refreshed.currentTeamId;
        if (stillNoTeam) {
          // Mirrors the reactive NO_TEAM branch: no workspace to retry in,
          // so leave the dead composer. The draft is already autosaved.
          toast.info(t('post_workspace_session_ended'));
          navigate('/agency');
          return;
        }
      } catch (refreshError) {
        devLog.error('[PostComposer] refreshUser failed (no team context)', refreshError);
      }
      toast.info(t('post_workspace_session_refreshed'));
      return;
    }

    // Success tail shared by the normal path and a create confirmed later via "Check again".
    const completeDraftSuccess = (toastMessage: string): void => {
      toast.success(toastMessage);

      // Clear auto-saved draft (local + server)
      clearDraftEverywhere();

      // Invalidate queries
      invalidateReleaseState(queryClient, {
        teamId,
        postId: useComposerStore.getState().editingPostId ?? undefined,
      });

      setSaveUiState('idle');
      onSuccess?.();
    };

    try {
      // Convert changedThumbnails Map to object for API
      const mediaThumbnails =
        store.changedThumbnails.size > 0 ? Object.fromEntries(store.changedThumbnails) : undefined;

      // `editingPostId` (not just edit mode): a post this composer already created is adopted, so a
      // second save is an UPDATE, never a second POST.
      if (store.editingPostId) {
        // Update existing post as unscheduled (draft)
        await updatePostResilient({
          id: store.editingPostId,
          data: {
            caption: store.caption,
            // Mapped (shooting-mode) post: the store holds NO channels (loadFromMapping), so sending []
            // would wipe post_channels. Leave them untouched, exactly as the other shooting-mode saves do.
            channelIds: isShootingMode ? undefined : store.selectedChannels,
            mediaIds: store.media.map((m) => m.id),
            mediaThumbnails,
            // No `status` on a save of an EXISTING post: the release state is not the composer's to write
            // (a reviewer-only person saving their own seed draft, or a creator fixing Changes requested,
            // must not be downgraded or refused for it). Only a NEW post is created as UNSCHEDULED.
            // Side effect: a save no longer "self-heals" the stale `post.status` cache back to UNSCHEDULED; state is read from the release/occurrence layers.
            labels: store.labels,
            calendarId: store.calendarId,
            calendarNoteId: store.calendarNoteId,
            calendarHolidayId: store.calendarHolidayId,
            isCarousel: store.isCarouselMode,
          },
        });
        devLog.log('[PostComposer] Draft updated successfully');
        completeDraftSuccess('Draft updated');
      } else {
        // Create new unscheduled post (draft)
        await createPostResilient(
          {
            caption: store.caption,
            channelIds: store.selectedChannels, // Can be empty for drafts
            mediaIds: store.media.map((m) => m.id),
            mediaThumbnails,
            status: 'UNSCHEDULED', // Unscheduled = draft
            labels: store.labels,
            calendarId: store.calendarId,
            calendarNoteId: store.calendarNoteId,
            calendarHolidayId: store.calendarHolidayId,
            isCarousel: store.isCarouselMode,
            plannedDate: store.scheduledTime?.toISOString(), // For calendar mapping when opened from calendar
          },
          async () => {
            completeDraftSuccess('Draft saved');
          }
        );
        devLog.log('[PostComposer] Draft saved successfully');
        completeDraftSuccess('Draft saved');
      }
    } catch (error) {
      if (error instanceof SaveNotConfirmedError) return;
      adoptFromCreateError(error);
      // Transient "No team context" (NO_TEAM) 403 — don't show the raw
      // backend message and don't discard the draft (nothing above this
      // point mutates `store`, so caption/media/labels are untouched).
      // Attempt a silent recovery via refreshUser() (re-fetches /me, which
      // re-asserts team context), then ask for a single guided retry rather
      // than auto-retrying the save ourselves (risk of a double-post).
      if (isNoTeamError(error)) {
        devLog.warn('[PostComposer] Save Draft got NO_TEAM — attempting session refresh', error);
        try {
          const refreshed = await refreshUser();
          const stillNoTeam =
            !refreshed.godModeSession && !refreshed.teamId && !refreshed.currentTeamId;
          if (stillNoTeam) {
            // No workspace to retry in (e.g. God Mode ended elsewhere) -
            // don't strand the user on a dead composer; go to the portfolio.
            // The draft is already autosaved locally/server-side.
            toast.info(t('post_workspace_session_ended'));
            navigate('/agency');
            return;
          }
        } catch (refreshError) {
          devLog.error('[PostComposer] refreshUser failed after NO_TEAM', refreshError);
        }
        toast.info(t('post_workspace_session_refreshed'));
        return;
      }

      const submitWarning = classifySubmitWarning(error, t, formatDateTime);
      if (submitWarning) {
        toast.warning(submitWarning.message);
        onError?.(new Error(submitWarning.message));
        return;
      }
      const message = getSaveErrorMessage(error, 'Failed to save draft');
      if (!isGlobalGatingHandledError(error)) toast.error(message);
      onError?.(error instanceof Error ? error : new Error(message));
    }
  };

  /**
   * Save and mark ready (for approval workflow)
   *
   * Note: Cannot delegate to handleSaveDraft() because it calls store.reset()
   * which clears editingPostId before we can mark-ready.
   */
  const handleSaveAndMarkReady = (): Promise<void> => runExclusiveSave(runSaveAndMarkReady);

  const runSaveAndMarkReady = async (): Promise<void> => {
    const validation = validateDraft();
    if (!validation.valid) {
      toast.error(validation.error || 'Validation failed');
      onError?.(new Error(validation.error));
      return;
    }

    // Steps 2+3 shared by the normal path and a create confirmed later via "Check again".
    const markReadyAndFinish = async (postId: number | null): Promise<void> => {
      // Step 2: Mark ready (postId is captured before any reset). A multi-post create has no single post to
      // mark: skipped, as before.
      if (postId !== null) {
        await apiClient.post(`/api/posts/${postId}/save-and-mark-ready`);
      }

      // Step 3: Clean up
      clearDraftEverywhere();

      // Invalidate queries
      invalidateReleaseState(queryClient, { teamId, postId: postId ?? undefined });

      setSaveUiState('idle');
      toast.success(postId === null ? t('common:saving_saved') : 'Post saved and marked ready for approval');
      onSuccess?.();
    };

    try {
      // Convert changedThumbnails Map to object for API
      const mediaThumbnails =
        store.changedThumbnails.size > 0 ? Object.fromEntries(store.changedThumbnails) : undefined;

      let postId = store.editingPostId;

      // Step 1: Save the post (create or update)
      if (postId) {
        await updatePostResilient({
          id: postId,
          data: {
            caption: store.caption,
            // Mapped (shooting-mode) post: the store holds NO channels and the mapping's
            // calendarId only (loadFromMapping). Re-sending them would wipe post_channels
            // and null the mapping's note/holiday link. Leave them untouched, exactly as
            // handlePrimaryAction does for shooting mode.
            channelIds: isShootingMode ? undefined : store.selectedChannels,
            mediaIds: store.media.map((m) => m.id),
            mediaThumbnails,
            labels: store.labels,
            calendarId: isShootingMode ? undefined : store.calendarId,
            calendarNoteId: isShootingMode ? undefined : store.calendarNoteId,
            calendarHolidayId: isShootingMode ? undefined : store.calendarHolidayId,
            isCarousel: store.isCarouselMode,
          },
        });
      } else {
        const response = await createPostResilient(
          {
            caption: store.caption,
            channelIds: store.selectedChannels,
            mediaIds: store.media.map((m) => m.id),
            mediaThumbnails,
            status: 'UNSCHEDULED',
            labels: store.labels,
            calendarId: store.calendarId,
            calendarNoteId: store.calendarNoteId,
            calendarHolidayId: store.calendarHolidayId,
            isCarousel: store.isCarouselMode,
            plannedDate: store.scheduledTime?.toISOString(),
          },
          async (lateCreated) => {
            await markReadyAndFinish(lateCreated.isMulti ? null : lateCreated.id);
          }
        );
        postId = response.isMulti ? null : response.id;
        await markReadyAndFinish(postId);
        return;
      }

      if (postId) {
        await markReadyAndFinish(postId);
      }
    } catch (error) {
      if (error instanceof SaveNotConfirmedError) return;
      adoptFromCreateError(error);
      const submitWarning = classifySubmitWarning(error, t, formatDateTime);
      if (submitWarning) {
        toast.warning(submitWarning.message);
        onError?.(new Error(submitWarning.message));
        return;
      }
      const message = getSaveErrorMessage(error, 'Failed to save and mark ready');
      if (!isGlobalGatingHandledError(error)) toast.error(message);
      onError?.(error instanceof Error ? error : new Error(message));
    }
  };

  /**
   * Save, then act on the saved post: send it for review, or release it directly. One flow for both,
   * for a NEW post (the create persists it, then the action runs on its id) and an EXISTING one (the
   * update is stored FIRST, so the reviewer / the release snapshot sees the fixed version). The save
   * goes through the same resilient create/update paths as every other save (idempotency key,
   * edit-version, single save lock). Gated in the UI by the server verdicts (`canSendForReview`,
   * `canReleaseDirect`).
   */
  const handleSaveAndSendForReview = (): Promise<void> => runExclusiveSave(() => runSaveThenAct('review'));
  const handleSaveAndRelease = (): Promise<void> => runExclusiveSave(() => runSaveThenAct('release'));

  const runSaveThenAct = async (action: 'review' | 'release'): Promise<void> => {
    const validation = validateDraft();
    if (!validation.valid) {
      toast.error(validation.error || 'Validation failed');
      onError?.(new Error(validation.error));
      return;
    }

    /** Step 2 + 3, shared by the normal path and a create confirmed later via "Check again". */
    const actAndFinish = async (postId: number | null): Promise<void> => {
      // A multi-post create has no single post to act on: the save stands, nothing is claimed beyond it.
      if (postId === null) {
        clearDraftEverywhere();
        invalidateReleaseState(queryClient, { teamId });
        setSaveUiState('idle');
        toast.success(t('common:saving_saved'));
        onSuccess?.();
        return;
      }

      if (action === 'review') {
        await approvalService.submitForApproval(String(postId));
        clearDraftEverywhere();
        setSaveUiState('idle');
        invalidateReleaseState(queryClient, { teamId, postId });
        announceReleaseMoment('sent');
        onSuccess?.();
        return;
      }

      // Release. The post IS saved at this point: a failure here is "Saved as draft", never "Released".
      let result: ReleaseResult;
      try {
        result = await approvalService.releaseDirect(postId);
      } catch (releaseError) {
        clearDraftEverywhere();
        setSaveUiState('idle');
        invalidateReleaseState(queryClient, { teamId, postId });
        toast.warning(
          t('common:composer_saved_not_released', { reason: getReleaseFailureReason(releaseError, i18n) })
        );
        onSuccess?.();
        return;
      }
      clearDraftEverywhere();
      setSaveUiState('idle');
      invalidateReleaseState(queryClient, { teamId, postId });
      announceRelease({
        postId,
        variationId: null,
        undoUntil: result.undoUntil,
        reviewedAt: result.reviewedAt,
        undoAvailable: result.undoAvailable,
      });
      announceReleaseMoment('released', 1, result.undoAvailable === false);
      onSuccess?.();
    };

    try {
      const mediaThumbnails =
        store.changedThumbnails.size > 0 ? Object.fromEntries(store.changedThumbnails) : undefined;

      // `editingPostId` (not just edit mode): a post this composer already created is adopted, so a
      // retry is an UPDATE, never a second POST.
      const existingId = store.editingPostId;

      if (existingId) {
        await updatePostResilient({
          id: existingId,
          data: {
            caption: store.caption,
            // Mapped (shooting-mode) post: leave channels / calendar links untouched (see handleSaveAndMarkReady)
            channelIds: isShootingMode ? undefined : store.selectedChannels,
            mediaIds: store.media.map((m) => m.id),
            mediaThumbnails,
            labels: store.labels,
            calendarId: isShootingMode ? undefined : store.calendarId,
            calendarNoteId: isShootingMode ? undefined : store.calendarNoteId,
            calendarHolidayId: isShootingMode ? undefined : store.calendarHolidayId,
            isCarousel: store.isCarouselMode,
          },
        });
        await actAndFinish(existingId);
        return;
      }

      const response = await createPostResilient(
        {
          caption: store.caption,
          channelIds: store.selectedChannels,
          mediaIds: store.media.map((m) => m.id),
          mediaThumbnails,
          status: 'UNSCHEDULED',
          labels: store.labels,
          calendarId: store.calendarId,
          calendarNoteId: store.calendarNoteId,
          calendarHolidayId: store.calendarHolidayId,
          isCarousel: store.isCarouselMode,
          plannedDate: store.scheduledTime?.toISOString(),
        },
        async (lateCreated) => {
          await actAndFinish(lateCreated.isMulti ? null : lateCreated.id);
        }
      );
      await actAndFinish(response.isMulti ? null : response.id);
    } catch (error) {
      if (error instanceof SaveNotConfirmedError) return;
      // A create that persisted before a later step failed: adopt it so the next submit is an UPDATE
      adoptFromCreateError(error);
      const submitWarning = classifySubmitWarning(error, t, formatDateTime);
      if (submitWarning) {
        toast.warning(submitWarning.message);
        onError?.(new Error(submitWarning.message));
        return;
      }
      const message = getSaveErrorMessage(
        error,
        action === 'release' ? 'Failed to save and release' : 'Failed to send for review'
      );
      if (!isGlobalGatingHandledError(error)) toast.error(message);
      // The save may have gone through: refresh so the composer shows the stored state
      const savedId = useComposerStore.getState().editingPostId;
      invalidateReleaseState(queryClient, { teamId, postId: savedId ?? undefined });
      onError?.(error instanceof Error ? error : new Error(message));
    }
  };

  /**
   * Discard draft and reset
   */
  const handleDiscard = () => {
    clearDraftEverywhere();
    saveEpochRef.current += 1;
    lateSuccessRef.current = null;
    pendingCreateRef.current = null;
    setSaveUiState('idle');
    store.reset();
  };

  /** Clear the save-resilience UI state (called when the composer closes and resets). */
  const resetSaveUiState = (): void => {
    saveEpochRef.current += 1;
    lateSuccessRef.current = null;
    pendingCreateRef.current = null;
    setSaveUiState('idle');
  };

  return {
    // Store state
    ...store,

    // Validation
    validate,
    validateDraft,
    canSubmit: canSubmit(),
    canSaveDraft: canSaveDraft(),

    // Actions
    handlePrimaryAction,
    handleSaveDraft,
    handleSaveAndMarkReady,
    handleSaveAndSendForReview,
    handleSaveAndRelease,
    handleDiscard,

    // Edit mode state
    isEditMode,
    editingPostId: store.editingPostId,
    originalPost: store.originalPost,
    existingPost,
    // Also loading until this open's fresh fetch has arrived and been loaded into the form (see isPostFreshForOpen).
    isLoadingPost: isLoadingPost || (isOpen && isEditMode && !loadPostError && !isPostFreshForOpen),
    loadPostError,
    retryLoadPost: () => {
      void refetchPost();
    },
    isLoadPostRefetching,

    // Shooting mode state (NEW)
    isShootingMode,
    mappingData,
    isLoadingMapping,
    loadMappingError,
    defaultShootMode,
    liveScheduledInstance,
    isLoadingScheduledInstance,

    // Loading states
    isPublishing: createPostMutation.isPending,
    isUpdating: updatePostMutation.isPending,
    // "Still saving…" (polling the outcome) counts as busy for every save trigger.
    isSaving:
      createPostMutation.isPending ||
      updatePostMutation.isPending ||
      saveUiState === 'still_saving' ||
      saveUiState === 'still_saving_keep_checking' ||
      isCheckingAgain,
    isCheckingAgain,
    saveUiState,
    checkSaveAgain,
    resetSaveUiState,
    getSaveErrorMessage,
    isShooting: shootMutation.isPending,

    // Error states
    error:
      createPostMutation.error ||
      updatePostMutation.error ||
      shootMutation.error ||
      loadPostError ||
      loadMappingError,
    isError:
      createPostMutation.isError ||
      updatePostMutation.isError ||
      shootMutation.isError ||
      !!loadPostError ||
      !!loadMappingError,

    // Draft management
    clearDraft: clearDraftEverywhere,
    getRestorableDraft,
    restoreDraft: applyDraftToStore,
    hasDraft: hasSaved(),
  };
}
